import { Box, Text } from '@byecode/ui'
import type { DataSourceAbstract, EVENT_VARIABLE_TYPE, IndependentAction, SubProcessArg } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import { innerTypeNameMap, TooltipText } from '@lighthouse/shared'
import type { JSONContent } from '@tiptap/core'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

import type { LoopInfo } from './SettingSubProcessParam'
import { SettingSubProcessParam } from './SettingSubProcessParam'

const SCxContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const SCxItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0;
`

const SCxLabel = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`

const SCxType = styled.div`
    width: 60px;
`

const SCxValue = styled.div`
    width: 100%;
`

interface SettingSubProcessContentProps {
    data: SubProcessArg[] | undefined
    dataSource?: DataSourceAbstract
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    parentNodes?: FlowNode[]
    loop?: LoopInfo
    eventType?: EVENT_VARIABLE_TYPE
    onChange?: (val: SubProcessArg[]) => void
}

export const SettingSubProcessContent: React.FC<SettingSubProcessContentProps> = ({
    data,
    dataSource,
    allParentNodes,
    allParentActions,
    parentNodes,
    loop,
    eventType,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const handleChange = useCallback(
        (val: JSONContent, index: number) => {
            if (!data) {
                return
            }
            const newData = data.map((item, i) => {
                if (i === index) {
                    return {
                        ...item,
                        value: val
                    }
                }
                return item
            })
            onChange?.(newData)
        },
        [onChange, data]
    )

    return (
        <SCxContainer>
            {data?.map((item, index) => (
                <SCxItem key={item.id}>
                    <SCxLabel>
                        <TooltipText
                            title={item.name}
                            render={ref => (
                                <Text ref={ref} inline size={14} color="var(--color-black)">
                                    {item.name}
                                </Text>
                            )}
                        />
                        <SCxType>
                            <Text size={14} color="var(--color-gray-400)">
                                [{innerTypeNameMap[item.innerType]}]
                            </Text>
                        </SCxType>
                    </SCxLabel>
                    <SCxValue>
                        <SettingSubProcessParam
                            innerType={item.innerType}
                            dataSource={dataSource}
                            value={item.value}
                            dataSourceList={dataSourceList}
                            allParentNodes={allParentNodes}
                            allParentActions={allParentActions}
                            loop={loop}
                            parentNodes={parentNodes}
                            eventType={eventType}
                            onChange={val => handleChange(val, index)}
                        />
                    </SCxValue>
                </SCxItem>
            ))}
        </SCxContainer>
    )
}
